import React, { useEffect, useRef, useState } from 'react'
import Countdown from 'react-countdown'
import successIcon from '../assets/images/notification_success.png'
import errorIcon from '../assets/images/error_red.svg'
import warningIcon from '../assets/images/warning_orange.svg'
import { IntegrationStatusDescription, formatDate } from '../utils/common'
import { useStateContext } from "../contexts/ContextProvider"
import { TaskType } from '../utils/enums'

window.nirvana.translator = {
    translateFn: (x) => x
};

const countdownRenderer = ({ minutes, seconds, completed }) => {
    if (completed) {
        // Render a completed state
        return <span className='font-sans text-sm font-medium'>Running</span>
    } else {
        // Render a countdown
        return <span>{minutes} min : {seconds} sec</span>;
    }
};

const DashboardGrid = ({ queryResult }) => {
    const [lastInventoryTaskDate, setLastInventoryTaskDate] = useState(null);
    const [lastAgentsTaskDate, setLastAgentsTaskDate] = useState(null);
    const [latestSyncTask, setLatestSyncTask] = useState(null);
    const [isAgentTaskSuccessful, setIsAgentTaskSuccessful] = useState(null);
    const [isInventoryTaskSuccessful, setIsInventoryTaskSuccessful] = useState(null);
    const [integrationStatus, setIntegrationStatus] = useState(null);
    const [hasUnhandledEvents, setHasUnhandledEvents] = useState(null);
    const [unhandledEventsCount, setUnhandledEventsCount] = useState(null);
    const [nextInventoryTaskRun, setNextInventoryTaskRun] = useState(null);
    const [nextAgentsTaskRun, setNextAgentsTaskRun] = useState(null);
    const { isRecurrentAgentTaskRunning, isRecurrentInventoryTaskRunning } = useStateContext();

    let countdownInventoryRef = useRef();
    let countdownAgentRef = useRef();

    useEffect(() => {
        //check inventory task result
        if (!queryResult.latestInventoryTaskResult?.createdDate || queryResult.latestInventoryTaskResult?.createdDate.trim() === '') {
            setLastInventoryTaskDate('N/A');
            setIsInventoryTaskSuccessful(true)
        } else {
            setLastInventoryTaskDate(formatDate(queryResult.latestInventoryTaskResult.createdDate));
        }
        //check agents task result
        if (!queryResult.latestAgentTaskResult?.createdDate || queryResult.latestAgentTaskResult?.createdDate.trim() === '') {
            setLastAgentsTaskDate('N/A');
            setIsAgentTaskSuccessful(true)
        } else {
            setLastAgentsTaskDate(formatDate(queryResult.latestAgentTaskResult.createdDate));
        }

        if (lastInventoryTaskDate === 'N/A' && lastAgentsTaskDate === 'N/A') {
            setLatestSyncTask('N/A');
            return;
        }

        if (queryResult.latestInventoryTaskResult?.taskStatusType === 4) {
            setIsInventoryTaskSuccessful(true);
        } else if (queryResult.latestInventoryTaskResult?.taskStatusType === 5) {
            setIsInventoryTaskSuccessful(false);
        }

        if (queryResult.latestAgentTaskResult?.taskStatusType === 4) {
            setIsAgentTaskSuccessful(true);
        } else if (queryResult.latestAgentTaskResult?.taskStatusType === 5) {
            setIsAgentTaskSuccessful(false);
        }

        if (lastInventoryTaskDate && lastAgentsTaskDate) {
            let lastInventoryTaskAsDate = new Date(lastInventoryTaskDate).getTime();
            let lastAgentsTaskAsDate = new Date(lastAgentsTaskDate).getTime();
            if (lastInventoryTaskAsDate <= lastAgentsTaskAsDate) {
                setLatestSyncTask(formatDate(new Date(lastAgentsTaskAsDate), true));
            } else {
                setLatestSyncTask(formatDate(new Date(lastInventoryTaskAsDate), true));
            }
        } else if (lastInventoryTaskDate) {
            setLatestSyncTask(formatDate(new Date(lastInventoryTaskDate), true));
        } else if (lastAgentsTaskDate) {
            setLatestSyncTask(formatDate(new Date(lastAgentsTaskDate), true));
        }
    }, [lastInventoryTaskDate, lastAgentsTaskDate, isAgentTaskSuccessful, isInventoryTaskSuccessful, queryResult])

    useEffect(() => {        
        if (queryResult.agentEventsCount === 'undefined' || queryResult.inventoryEventsCount === 'undefined') {
            setHasUnhandledEvents(false);
            setUnhandledEventsCount('N/A');
        }
        else if (queryResult.agentEventsCount === 0 && queryResult.inventoryEventsCount === 0) {
            setHasUnhandledEvents(false);
            setUnhandledEventsCount('None');
        } else {
            let unhandledEventsCount = queryResult.agentEventsCount + queryResult.inventoryEventsCount;
            setHasUnhandledEvents(true);
            setUnhandledEventsCount(unhandledEventsCount);
            setIntegrationStatus(IntegrationStatusDescription.EVENTS_NOT_HANDLED.description);
        }

        if ((lastInventoryTaskDate !== 'N/A' && !isInventoryTaskSuccessful) || (lastAgentsTaskDate !== 'N/A' && !isAgentTaskSuccessful)) {
            setIntegrationStatus(IntegrationStatusDescription.TASKS_FAILED.description);
            setHasUnhandledEvents(false);
            setUnhandledEventsCount('None');
        } else {
            setIntegrationStatus(IntegrationStatusDescription.UP_AND_RUNNING.description);
        }

    }, [lastInventoryTaskDate, lastAgentsTaskDate, isAgentTaskSuccessful, isInventoryTaskSuccessful, hasUnhandledEvents, unhandledEventsCount, queryResult])

    useEffect(() => {
        if (queryResult.nextAgentRecurrentTaskRun) {
            setNextAgentsTaskRun(queryResult.nextAgentRecurrentTaskRun);
        }
        if (queryResult.nextInventoryRecurrentTaskRun) {
            setNextInventoryTaskRun(queryResult.nextInventoryRecurrentTaskRun);
        }
    }, [nextAgentsTaskRun, nextInventoryTaskRun, isRecurrentAgentTaskRunning, isRecurrentInventoryTaskRunning, queryResult])

    const triggerNewCountdown = (taskType) => {
        if (taskType === TaskType.INVENTORY) {
            setTimeout(() => {
                countdownInventoryRef.current.start();
            }, 2000)
        }
        if (taskType === TaskType.AGENT) {
            setTimeout(() => {
                countdownAgentRef.current.start();
            }, 2000)
        }
    }

    useEffect(() => {
        if (!isRecurrentInventoryTaskRunning && countdownInventoryRef.current) {
            let isStopped = countdownInventoryRef.current.isStopped();
            if (isStopped) {
                triggerNewCountdown(TaskType.INVENTORY);
            }
        }
        if (!isRecurrentAgentTaskRunning && countdownAgentRef.current) {
            let isAgentStopped = countdownAgentRef.current.isStopped();
            if (isAgentStopped) {
                triggerNewCountdown(TaskType.AGENT);
            }
        }
    }, [isRecurrentInventoryTaskRunning, isRecurrentAgentTaskRunning]);

    const handleInventoryCountdownCompletionState = () => {
        setTimeout(() => {
            countdownInventoryRef.current.stop();
        }, [2000]);
    }
    const handleAgentCountdownCompletionState = () => {
        setTimeout(() => {
            countdownAgentRef.current.stop();
        }, [2000])
    }

    return (
        <div className='flex flex-row items-center gap-4 flex-wrap'>
            <div className='card w-[400px] h-[206px]' slot='cards-base'>
                <gz-generic-widget state='is-active'>
                    <div className='flex flex-col mt-6 ml-6'>
                        <span className='font-sans text-base/5'>Bitdefender plugin status</span>
                        <div className='mt-4 flex flex-row items-center'>
                            {(isInventoryTaskSuccessful && isAgentTaskSuccessful && !hasUnhandledEvents) && <img className='mr-4 w-4 h-4' src={successIcon} alt='' />}
                            {(isInventoryTaskSuccessful && isAgentTaskSuccessful && hasUnhandledEvents) && <gz-tooltip
                                tooltipText='Unhandled events generated. Visit the Inventory / Agent Events pages for details.' tooltipClass='warning'>
                                <img className='mr-4 w-6 h-6' src={warningIcon} alt='' />
                            </gz-tooltip>}
                            {(!isInventoryTaskSuccessful || !isAgentTaskSuccessful) && <img className='mr-4 w-6 h-6' src={errorIcon} alt='' />}
                            <span className='font-sans text-sm font-medium'>{integrationStatus}</span>
                        </div>
                    </div>
                </gz-generic-widget>
            </div>
            <div className='card w-[400px] h-[206px]' slot='cards-base'>
                <gz-generic-widget state='is-active'>
                    <div className='flex flex-col mt-6 ml-6'>
                        <span className='font-sans text-base/5'>Last synchronization task</span>
                        <div className='mt-3 flex flex-row'>
                            <span className='font-sans text-2xl font-normal'>{latestSyncTask}</span>
                        </div>
                        <div className='mt-8 flex flex-row items-center justify-between mr-6 border-b-1'>
                            <span className='font-sans text-sm font-normal'>Inventory</span>
                            <div className='flex flex-row'>
                                <span className='font-sans text-sm font-normal flex flex-row items-center'>{lastInventoryTaskDate}</span>
                                {(lastInventoryTaskDate !== 'Never' && isInventoryTaskSuccessful) && <gz-tooltip tooltipText='Task succeeded' tooltipClass='success'>
                                    <img className='ml-4 w-4 h-4' src={successIcon} alt='successInventoryTask' />
                                </gz-tooltip>}
                                {(lastInventoryTaskDate !== 'Never' && !isInventoryTaskSuccessful) && <gz-tooltip tooltipText='Task failed' tooltipClass='error'>
                                    <img className='ml-4 w-6 h-6' src={errorIcon} alt='failedInventoryTask' />
                                </gz-tooltip>}
                            </div>
                        </div>
                        <div className='mt-3 flex flex-row items-center justify-between mr-6 border-b-1'>
                            <span className='font-sans text-sm font-normal'>Agents</span>
                            <div className='flex flex-row'>
                                <span className='font-sans text-sm font-normal flex flex-row items-center'>{lastAgentsTaskDate}</span>
                                {(lastAgentsTaskDate !== 'Never' && isAgentTaskSuccessful) && <gz-tooltip tooltipText='Task succeeded' tooltipClass='success'>
                                    <img className='ml-4 w-4 h-4' src={successIcon} alt='successAgentTask' />
                                </gz-tooltip>}
                                {(lastAgentsTaskDate !== 'Never' && !isAgentTaskSuccessful) && <gz-tooltip tooltipText='Task failed' tooltipClass='error'>
                                    <img className='ml-4 w-6 h-6' src={errorIcon} alt='failedAgentTask' />
                                </gz-tooltip>}
                            </div>
                        </div>
                    </div>
                </gz-generic-widget>
            </div>
            <div className='card w-[400px] h-[206px]' slot='cards-base'>
                <gz-generic-widget state='is-active'>
                    <div className='flex flex-col mt-6 ml-6'>
                        <span className='font-sans text-base/5'>Synchronization events</span>
                        <div className='mt-3 flex flex-row'>
                            <span className='font-sans text-2xl font-normal'>{unhandledEventsCount}</span>
                        </div>
                        <div className='mt-8 flex flex-row justify-between mr-6 border-b-1'>
                            <span className='font-sans text-sm font-normal'>Inventory</span>
                            <div className='flex flex-row'>
                                <span className='font-sans text-sm font-normal'>{queryResult.inventoryEventsCount ?? 'N/A'}</span>
                            </div>
                        </div>
                        <div className='mt-3 flex flex-row justify-between mr-6 border-b-1'>
                            <span className='font-sans text-sm font-normal'>Agents</span>
                            <div className='flex flex-row'>
                                <span className='font-sans text-sm font-normal'>{queryResult.agentEventsCount ?? 'N/A'}</span>
                            </div>
                        </div>
                    </div>
                </gz-generic-widget>
            </div>
            <div className='card w-[400px] h-[206px]' slot='cards-base'>
                <gz-generic-widget state='is-active'>
                    <div className='flex flex-col mt-6 ml-6'>
                        <span className='font-sans text-base/5'>Next recurrent synchronization task</span>
                        <div className='mt-8 flex flex-row justify-between mr-6 border-b-1'>
                            <span className='mt-8 font-sans text-sm font-normal'>Inventory</span>
                            <div className='mt-8 flex flex-row'>
                                {nextInventoryTaskRun && <Countdown ref={countdownInventoryRef}
                                    date={nextInventoryTaskRun}
                                    renderer={countdownRenderer}
                                    onComplete={handleInventoryCountdownCompletionState}>
                                </Countdown>}
                                {!nextInventoryTaskRun && <span>Never</span>}
                            </div>
                        </div>
                        <div className='mt-4 flex flex-row justify-between mr-6 border-b-1'>
                            <span className='font-sans text-sm font-normal'>Agents</span>
                            <div className='flex flex-row'>
                                {nextAgentsTaskRun && <Countdown ref={countdownAgentRef}
                                    date={nextAgentsTaskRun}
                                    renderer={countdownRenderer}
                                    onComplete={handleAgentCountdownCompletionState}>
                                </Countdown>}
                                {!nextAgentsTaskRun && <span>Never</span>}
                            </div>
                        </div>
                    </div>
                </gz-generic-widget>
            </div>
            <div className='card w-[400px] h-[206px]' slot='cards-base'>
                <gz-generic-widget state='is-active'>
                    <div className='flex flex-col mt-6 ml-6'>
                        <span className='font-sans text-base/5'>Total agents</span>
                        <div className='mt-3 flex flex-row'>
                            <span className='font-sans text-2xl font-medium'>{queryResult.agentsCount ?? 'N/A'}</span>
                        </div>
                    </div>
                </gz-generic-widget>
            </div>
        </div>
    )
}

export default DashboardGrid